import React, { useState } from 'react';
import { DollarSign, Users, Calculator, TrendingUp } from 'lucide-react';

export default function BenefitsCalculator() {
  var _salary = useState('');
  var salary = _salary[0], setSalary = _salary[1];
  var _family = useState('single');
  var family = _family[0], setFamily = _family[1];
  var _employer401k = useState(true);
  var employer401k = _employer401k[0], setEmployer401k = _employer401k[1];
  var _hsa = useState(true);
  var hsa = _hsa[0], setHsa = _hsa[1];
  var _commuter = useState(false);
  var commuter = _commuter[0], setCommuter = _commuter[1];
  var _fsa = useState(false);
  var fsa = _fsa[0], setFsa = _fsa[1];
  var _results = useState(null);
  var results = _results[0], setResults = _results[1];

  function calculate() {
    var s = parseFloat(salary) || 0;
    if (s === 0) return;

    var taxRate = s > 150000 ? 0.32 : s > 89075 ? 0.24 : s > 41775 ? 0.22 : 0.12;
    var items = [];

    if (employer401k) {
      var match = Math.min(s * 0.06, 23500) * 0.5;
      var taxSaved = Math.min(s * 0.06, 23500) * taxRate;
      items.push({ name: '401(k) Employer Match', amount: Math.round(match), note: 'Assuming 50% match up to 6%' });
      items.push({ name: '401(k) Tax Savings', amount: Math.round(taxSaved), note: 'Pre-tax contribution savings' });
    }

    if (hsa) {
      var limit = family === 'single' ? 4300 : 8550;
      var hsaSaved = Math.round(limit * taxRate);
      items.push({ name: 'HSA Tax Savings', amount: hsaSaved, note: 'Max contribution: $' + limit.toLocaleString() });
    }

    if (commuter) {
      var annual = 3360;
      items.push({ name: 'Commuter Benefits', amount: Math.round(annual * taxRate), note: 'Pre-tax transit/parking $280/mo' });
    }

    if (fsa) {
      var fsaLimit = family === 'single' ? 3300 : 5000;
      items.push({ name: 'Dependent Care FSA', amount: Math.round(fsaLimit * taxRate), note: 'Max: $' + fsaLimit.toLocaleString() });
    }

    var total = items.reduce(function(sum, i) { return sum + i.amount; }, 0);
    setResults({ items: items, total: total, taxRate: Math.round(taxRate * 100) });
  }

  var form = React.createElement('div', { className: 'space-y-5' },
    React.createElement('div', null,
      React.createElement('label', { className: 'block text-sm font-medium text-gray-700 mb-1' }, 'Annual Salary'),
      React.createElement('div', { className: 'relative' },
        React.createElement(DollarSign, { size: 16, className: 'absolute left-3 top-3.5 text-gray-400' }),
        React.createElement('input', {
          type: 'number', value: salary, placeholder: '85000',
          onChange: function(e) { setSalary(e.target.value); },
          className: 'w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none text-gray-900'
        })
      )
    ),
    React.createElement('div', null,
      React.createElement('label', { className: 'block text-sm font-medium text-gray-700 mb-1' }, 'Coverage Type'),
      React.createElement('div', { className: 'grid grid-cols-3 gap-2' },
        ['single', 'spouse', 'family'].map(function(opt) {
          return React.createElement('button', {
            key: opt, type: 'button',
            onClick: function() { setFamily(opt); },
            className: 'py-2 px-3 rounded-lg text-sm font-medium transition-all ' + (family === opt ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
          }, opt.charAt(0).toUpperCase() + opt.slice(1));
        })
      )
    ),
    React.createElement('div', null,
      React.createElement('label', { className: 'block text-sm font-medium text-gray-700 mb-2' }, 'Available Benefits'),
      React.createElement('div', { className: 'space-y-2' },
        [
          { key: 'employer401k', val: employer401k, set: setEmployer401k, label: '401(k) with Employer Match' },
          { key: 'hsa', val: hsa, set: setHsa, label: 'Health Savings Account (HSA)' },
          { key: 'commuter', val: commuter, set: setCommuter, label: 'Commuter Benefits' },
          { key: 'fsa', val: fsa, set: setFsa, label: 'Dependent Care FSA' },
        ].map(function(item) {
          return React.createElement('label', { key: item.key, className: 'flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50' },
            React.createElement('input', {
              type: 'checkbox', checked: item.val,
              onChange: function() { item.set(!item.val); },
              className: 'w-4 h-4 text-blue-600 rounded focus:ring-blue-500'
            }),
            React.createElement('span', { className: 'text-sm text-gray-700' }, item.label)
          );
        })
      )
    ),
    React.createElement('button', {
      onClick: calculate,
      disabled: !salary,
      className: 'w-full bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed py-3 rounded-xl font-bold text-lg shadow-lg transition-all flex items-center justify-center gap-2'
    }, React.createElement(Calculator, { size: 20 }), ' Calculate My Savings')
  );

  var resultsPanel = results ? React.createElement('div', { className: 'bg-gradient-to-br from-green-600 to-green-500 text-white rounded-3xl p-8 md:p-10 shadow-2xl' },
    React.createElement('div', { className: 'text-sm uppercase tracking-wide opacity-90 mb-1' }, 'Your Estimated Annual Savings'),
    React.createElement('div', { className: 'text-5xl md:text-7xl font-bold font-mono mb-2' }, '$' + results.total.toLocaleString()),
    React.createElement('div', { className: 'text-sm opacity-75 mb-6' }, 'Based on ' + results.taxRate + '% marginal tax rate'),
    React.createElement('div', { className: 'space-y-3 bg-white/10 rounded-2xl p-5 backdrop-blur-sm' },
      results.items.map(function(item, i) {
        return React.createElement('div', { key: i, className: 'flex justify-between items-center pb-3 ' + (i < results.items.length - 1 ? 'border-b border-white/20' : '') },
          React.createElement('div', null,
            React.createElement('span', { className: 'opacity-90 text-sm' }, item.name),
            React.createElement('div', { className: 'text-xs opacity-60' }, item.note)
          ),
          React.createElement('span', { className: 'font-mono font-bold text-lg' }, '$' + item.amount.toLocaleString())
        );
      })
    ),
    React.createElement('div', { className: 'mt-6 text-center' },
      React.createElement('a', { href: '/contact', className: 'inline-block bg-white text-green-700 hover:bg-gray-100 px-6 py-3 rounded-xl font-bold transition-all' }, 'Get Personalized Advice')
    )
  ) : React.createElement('div', { className: 'bg-gradient-to-br from-green-600 to-green-500 text-white rounded-3xl p-8 md:p-10 shadow-2xl flex flex-col items-center justify-center min-h-[400px]' },
    React.createElement(TrendingUp, { size: 64, className: 'opacity-30 mb-4' }),
    React.createElement('p', { className: 'text-xl font-bold mb-2' }, 'Enter your details'),
    React.createElement('p', { className: 'opacity-70 text-center' }, 'Fill in your salary and benefits to see your personalized savings estimate')
  );

  return React.createElement('div', { className: 'grid grid-cols-1 lg:grid-cols-2 gap-12 items-start' },
    React.createElement('div', null,
      React.createElement('h2', { className: 'text-4xl md:text-5xl font-bold mb-6 text-gray-900' }, 'Calculate Your Savings'),
      React.createElement('p', { className: 'text-xl text-gray-600 mb-8 leading-relaxed' }, 'Enter your details below to see how much you could save by maximizing your employee benefits.'),
      form
    ),
    resultsPanel
  );
}
